import { useState, useEffect, useCallback, useRef } from "react";
import {
  mergePanelSignature,
  // #2810 — the shared pre-flight validators, run here BEFORE an op is
  // enqueued (packages/score-common/src/utils/pre-flight.js). Same
  // functions score-api's scoresheet.js handler now calls too, so a
  // refusal this hook can already see coming is never queued as a doomed
  // network round trip. See docs/score/scribe-resilience.md gap G2.
  preflightCreateScoreSheet,
  preflightUpdateScoreSheet,
  preflightConfirmOrCompleteScoreSheet,
  effectiveTestForClass,
  // #2972 - a same-seat (rideId, judgeId) collision is stored server-side as
  // a CONTENDER (#2815), so every local lookup keyed on rideId+judgeId can
  // return more than one row. This hook is the scribe's own sheet's writer,
  // so picking an arbitrary one (or a contender) would silently start
  // autosaving into a sheet no reader shows.
  isAuthoritativeScoreSheet,
} from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { getManualRideById } from "../store/manualRides";
import {
  classes as classesCache,
  enqueue,
  getOutboxOp,
  horses as horsesCache,
  people as peopleCache,
  rides as ridesCache,
  scoreSheets,
  tests as testsCache,
} from "../local/db";
import { syncEngine } from "../local/sync";
import { markUnsavedEdit, clearUnsavedEdit, hasUnsavedEdit } from "../local/unsavedEdits";
import { subscribeToSheetContentReplaced } from "../local/sheetContentEvents";
import { deepEqual } from "../local/threeWayMerge";
import { useScoreContext } from "../context/ScoreContext";
import { normalizeRemarks } from "../util/remarks";
import { scoreSheetIsClientTotal } from "../local/scoreSheetScoringModel";

// Whole-sheet save is debounced so rapid mark entry collapses into one write.
const SAVE_DEBOUNCE_MS = 800;

// #3008 round 11 -- the load effect's unmount cleanup below (round 10) fires
// a fire-and-forget persist() when a debounced save was genuinely still
// pending at unmount, so the scribe's edit is never dropped. That promise is
// intentionally NOT awaited there (a cleanup function can't await), but it
// keeps running on the microtask queue after the component, and this
// module's own inflight.current ref, are both gone -- there is otherwise no
// way for anything OUTSIDE this hook instance to know the flush is still in
// flight. A component test's automatic unmount (cleanup()) can trigger
// exactly this flush (any test that types into a cell and ends before the
// 800ms debounce fires), and the next test's own fresh render/mock state can
// then race a STALE write from the PREVIOUS test's flush landing mid-test --
// this module-level registry lets a test harness explicitly wait for every
// outstanding unmount flush to settle before moving on, the same shape as
// this file's own unsavedEdits.js/sheetContentEvents.js test-only reset
// hooks.
const pendingUnmountFlushes = new Set();
export async function _waitForPendingUnmountFlushesForTests() {
  // Loop: a flush's own persist() can itself chain further work (retries,
  // the outbox drain) that adds to the set after this snapshot; draining
  // until the set is empty catches that instead of racing a single pass.
  while (pendingUnmountFlushes.size > 0) {
    await Promise.allSettled([...pendingUnmountFlushes]);
  }
}

// score-api owns these; never send them back on a save (the server recomputes
// totals on complete and stamps its own timestamps/id/org).
const SERVER_MANAGED = [
  "id",
  "orgId",
  "createdAt",
  "updatedAt",
  "totalPoints",
  "maxPoints",
  "percentage",
  // #1751 — panel signatures are written ONLY by POST /scoresheet/:id/
  // panel-signature (score-api lists `signatures` in SERVER_ONLY_FIELDS, so a
  // PUT could never set them anyway). signPanel() merges an OPTIMISTIC entry
  // into local state with `signatureHash: null` — only the server knows the
  // real S3 content address — and that entry would fail the sheet schema if a
  // debounced save carried it back up.
  "signatures",
  // #2756 — the local-cache-only flag on persist()'s provisional
  // entityKey-keyed row. Nothing server-side knows it; belt-and-braces strip
  // so it can never reach a create/update body even if a cache row ever
  // leaked into sheet state.
  "provisional",
  // #3008 -- request-only field toBody() below sets explicitly from the
  // tracked ancestor (_syncedSheet), never carried through from sheet state
  // itself (which may still carry a previous call's value via the local
  // cache-row merge persist() does after enqueueing).
  "baseVersion",
  // #3008 judge round 2 (B2) -- the local cache row's tracked ancestor.
  // Sheet state seeded from a cache row (fromCache) carries it, so without
  // this strip every save PUT the whole previous ancestor to score-api as
  // an unknown field; an echoing response then nested it inside the next
  // ancestor stamp and wedged the row as permanently "diverged".
  "_syncedSheet",
];

// #2472 — score-api's own SERVER_ONLY_FIELDS (scoresheet.js ~line 175-176),
// #2406 stamps ONCE at createScoreSheet from the class's paperEntry option
// and never again (score-common's scoreSheetSchema comment: "Stamped once,
// at create... that is deliberate, not a bug"). A sheet assembled locally
// for a ride that has never had a sheet (assemble()'s synthesized base, no
// serverSheet yet) carries neither key, and #906's never-downgrade content
// protection (this hook's load-effect `keepLocal`/`keepSheet` guard) means
// nothing ever re-seats latestSheet.current from a fresh server row for the
// rest of that mount — so without this, a sheet CREATED in-session never
// learns its own stamp until a genuine ride switch or reload re-assembles
// from scratch. Merging just these two into the in-memory sheet post-flush
// (see persist()'s flush().then() below) is safe against #906 specifically
// BECAUSE they are immutable and never scribe-editable: score-api's update
// path restore loop (~line 1139-1147) unconditionally forces them back to
// existing[field], and createScoreSheet (~line 829-831) spreads numericOnly
// after parsed.data to ensure a client body cannot set either. Unlike sheet
// CONTENT (marks/collectives/remarks), there is no "just-typed" value here
// that a slower background read could ever clobber.
const SERVER_ONLY_FIELDS = ["numericOnly", "noSignatureNeeded"];

// #1750 / equitation-class-sheet-design.md §11 — a judge-percentage sheet
// (the class-grid layout) has no movement math to derive a total from: the
// Total cell IS the score of record, typed directly by the scribe. Every
// other scoring model keeps percentage server-computed-only. Never widen
// this beyond the one model, and never drop the guard for anyone else.
// #3008 judge round 1 — moved to its own module (scoreSheetScoringModel.js,
// imported above) so sync.js's stale_write auto-merge (threeWayMerge.js)
// can import this SAME predicate rather than re-deriving it (a sync.js <->
// useScoreSheet.js direct import would cycle: sync.js already exports
// `syncEngine`, which this hook imports).

// #698 — a PUT rejected because the sheet is already confirmed/signed comes
// back from score-api as a 409 (server-enforced numeric freeze, #697).
// Translate that into copy a scribe can act on rather than a raw code. Any
// other failure (network blip, 5xx) gets a generic retry message — the
// outbox keeps retrying it regardless (see flush()'s retry loop).
// #1605 — takes the outbox op itself (httpStatus/errorClass/status), not just
// lastError text. lastError text-matching for "401"/"403"/"409" never
// actually worked in production: common-fe's installServerErrorInterceptor
// (installed for every call in scoreApi.js) rewrites err.message to the
// generic SERVER_ERROR_MESSAGE ("Server Error") before sync.js's catch block
// ever stores it, so none of those digits are in lastError to match against.
// httpStatus/errorClass are captured separately in sync.js's flush() for
// exactly this reason (see its own comment) and are what outboxDetail.js's
// isNotAssignedScribeError already keys on for the sync-detail dialog — this
// brings the per-sheet banner in line with that, instead of duplicating a
// second, broken text-matching scheme.
// Exported for direct unit testing (useScoreSheet.test.js) — the hook itself
// is not a good surface to drive this branch logic through.
export function describeSaveError(op) {
  const httpStatus = op?.httpStatus ?? null;
  const errorClass = op?.errorClass ?? null;
  if (httpStatus === 409) {
    return "This sheet's marks are already confirmed (or signed) and can no longer be changed. Comments still save normally.";
  }
  // #1289 — a 401 is a dead session, not a network problem. Saying
  // "once back online" here (while the header chip reads Online) is what sent
  // two people debugging connectivity for an hour on 2026-07-28. Name the
  // actual cause and the actual action.
  if (errorClass === "auth") {
    return "Your session expired. Sign in again to save — your work is safe on this device until you do.";
  }
  // #1562/#1605 — a 403 is NOT a dead session: the token verified fine and
  // score-api is refusing this scribe on purpose, almost always because
  // they're no longer the assigned scribe for this class/position. Sending
  // them to sign in again fixes nothing and costs them the sheet mid-ride
  // (this is the exact bug #1562 killed for the outbox chip/badge — this
  // banner was a second, separate surface still doing it). Name the real,
  // actionable fix instead.
  if (httpStatus === 403) {
    return "You're signed in, but not allowed to save this sheet — you may no longer be the assigned scribe for this class. Your work is safe on this device; ask your show manager to fix it.";
  }
  // Any other permanent (terminal) failure: sync.js's flush() marks it
  // "terminal" and never retries it again (see isSurfacedError/#1921) — don't
  // tell the scribe it's retrying when it categorically is not.
  if (op?.status === "terminal") {
    return "Save failed and won't retry on its own. Your work is safe on this device — escalate to your show manager.";
  }
  // Deliberately says nothing about being online: this branch covers network
  // blips AND 5xx AND anything unclassified, and the outbox retries all of
  // them regardless of connectivity.
  return "Save failed — it will keep retrying automatically.";
}

// #1605 — whether a just-flushed outbox op should populate the per-sheet
// saveError banner at all. Pulled out of the call site (like outboxDetail.js
// pulls isHeldForDisplay/isNotAssignedScribeError out of SyncStatus.jsx) so
// it's independently testable: #1921 routed every non-401 4xx (409, 403,
// validation) to outbox status "terminal" rather than "error", but this
// gate's pre-#1921 form checked "error" alone, which meant describeSaveError
// was never even called for a 403 (or a 409) — the banner just stayed empty.
export function isSaveErrorOp(status) {
  return status === "error" || status === "terminal";
}

// #2972 judge round 3 (driver rule) - THIS device's own server sheet for a
// (ride, judge) seat, resolved from that seat's cached rows. Its own lost-race
// CONTENDER comes FIRST: sync.js's markProvisionalSheetAsContender stamps
// contenderServerId onto the provisional entityKey row once this device's
// create loses the #2815 same-seat race, and from then on that id IS this
// scribe's sheet. fromApi() caches every sheet on the ride, so the winner's
// authoritative sheet usually sits in the same cache; an authoritative-first
// lookup would hand this scribe the WINNER's id (every later autosave,
// complete, confirm and sign then overwrites the winner, the round-1 bug).
// Only a device that owns no contender falls through to the seat's
// authoritative, non-provisional row. Same order as sync.js's
// serverSheetIdOf. `row` is the cached server row for `id` when one is
// cached (null otherwise, e.g. a contender whose row is not cached yet).
async function resolveOwnCachedServerRow(rows, entityKey, judgeId) {
  const stamped =
    rows.find((s) => s.provisional && s.contenderServerId) ??
    (entityKey ? await scoreSheets.get(entityKey).catch(() => null) : null);
  const contenderId = stamped?.contenderServerId ?? null;
  if (contenderId) {
    return {
      id: contenderId,
      row: rows.find((s) => s.id === contenderId && !s.provisional) ?? null,
    };
  }
  const row =
    rows.find(
      (s) => s.judgeId === judgeId && !s.provisional && isAuthoritativeScoreSheet(s),
    ) ?? null;
  return { id: row?.id ?? null, row };
}

// Map the local sheet to a create/update body: drop server-managed + undefined
// keys. The remaining shape matches score-common's scoreSheetSchema.
// `test` is the resolved Test for this sheet (may be null while still
// loading) — passed so percentage can be let through for the one scoring
// model that owns it client-side (see scoreSheetIsClientTotal above).
function toBody(sheet, test, ancestor) {
  const clientTotal = scoreSheetIsClientTotal(test);
  const body = {};
  for (const [k, v] of Object.entries(sheet)) {
    if (k === "percentage" && clientTotal) {
      if (v !== undefined) body[k] = v;
      continue;
    }
    if (SERVER_MANAGED.includes(k)) continue;
    if (v !== undefined) body[k] = v;
  }
  // #3008 -- score-api's stale_write check reads a request-only baseVersion
  // field, never the stored `version` (stripped above; never refreshed
  // client side otherwise). `ancestor` is the local row's tracked
  // `_syncedSheet` -- the sheet exactly as last returned by the server, the
  // boundary THIS edit is actually based on. Only set when one exists: an
  // unsynced create has none yet, and omitting it skips the server's check
  // entirely (back-compat, every other client still omits it today).
  if (ancestor?.version !== undefined) body.baseVersion = ancestor.version;
  return body;
}

/**
 * Loads the ScoreSheet for a (rideId, judgeId) pair from score-api — hydrating
 * ride/class/test/horse/rider — and returns it plus mutators. Writes are the
 * card #364 "simplified offline sync": every edit debounces a whole-sheet save
 * to the API; if the save fails (offline), the entire sheet is parked in
 * IndexedDB (last-write-wins) and replayed on reconnect, so nothing is lost.
 *
 * judgeId is the scribe's judge position (C/E/H/M/B) for the class — one judge
 * per position, so the position is a stable per-ride grouping key. (FVD gives
 * judge names, not Person ids; resolving real judge ids is deferred.)
 *
 * #1888 investigation finding: `loading` only flips back to true for a
 * genuine new sheet (a real ride/judge switch, or first mount) — NOT for a
 * same-sheet revalidation, which includes every re-run this hook's load
 * effect does when `online` flips (a reconnect drain, or a real venue-wifi
 * blip). ScoringForm gates its whole tree behind `if (loading ||
 * resolvingPosition) return <spinner>` — TWO inputs, not one. This hook only
 * owns `loading`; `resolvingPosition` comes from useAssignedPosition
 * (apps/scribe-fe/src/hooks/useAssignedPosition.js), which had the SAME bug
 * (an unconditional `loading: true` on every `online`-triggered re-run) and
 * is fixed the same way (a same-key revalidation guard). Any caller that
 * lets EITHER hook's loading-shaped value go back to a "not ready yet" state
 * on a mere revalidation will unmount/remount the scoring form again — same
 * class of bug as #153/#154 (scroll reset, focus loss, visible flash). A
 * future per-tick value (e.g. #1889's venue clock) must live OUTSIDE both
 * hooks' state/effects entirely, not as a new dependency of either load
 * effect or a new field that forces either hook's loading value truthy.
 *
 * #1781 — `preloaded` (optional) lets a caller that already has the ride,
 * class and/or test hand them in so this hook skips fetching them itself.
 * ClassGrid mounts one useScoreSheet per row (one per rider), and its own
 * useRidesForClass/useClassGridTest have already fetched the class and every
 * ride in it plus resolved the test — without this, each row redundantly
 * repeated all three fetches, turning a 25-rider class into ~150 requests on
 * mount over venue wifi. Any param left unset (every other caller, e.g.
 * ScoringForm) falls through to this hook's own fetch exactly as before —
 * getHorse/getPerson/listScoreSheets are NOT covered here (deferred, see the
 * #1781 card comment) since they are per-ride identity/sheet data the caller
 * does not have grouped by ride.
 *
 * #2191 — `options.allowNoJudge` (optional, default false) lets a caller
 * whose judgeId will NEVER resolve (a never-assigned scribe reaching the
 * Start Scoring gate for a not-final ride via ScoringForm's `unassigned`
 * fall-through: no seat of their own, and no viewPosition either — that's
 * only auto-picked once the ride is FINAL) load ride/klass/test read-only
 * instead of staying in `loading: true` forever. Without this flag, a null
 * judgeId means "hasn't resolved YET" (#627 — the seat arrives a tick after
 * the ride id) and the load effect below deliberately never assembles a
 * judge-less sheet. WITH it, null judgeId means "there is no seat, full
 * stop" — sheet fetching/saving still requires a real judgeId everywhere
 * else in this hook (persist/complete/etc. all bail on `!judgeId`), so this
 * only ever produces a read-only, hasStarted:false, isCompleted:false view.
 */
export function useScoreSheet(rideId, judgeId, preloaded = {}, options = {}) {
  const { allowNoJudge = false } = options;
  const preloadedRide = preloaded?.ride ?? null;
  const preloadedKlass = preloaded?.klass ?? null;
  const preloadedTest = preloaded?.test ?? null;
  const api = useScoreApi();
  const { online } = useScoreContext();
  const [state, setState] = useState({
    loading: true,
    error: null,
    ride: null,
    klass: null,
    test: null,
    horse: null,
    rider: null,
    judgeName: null,
    sheet: null,
    isCompleted: false,
    isConfirmed: false,
    // #901 — true once this sheet has EVER been signed (server-stamped
    // everSignedAt, which reopenScoreSheet does not clear). Drives the
    // reopened-sheet "Save" footer in ScoringForm.
    wasEverSigned: false,
    saveError: null,
    // #2192 — gates ScoringForm's Start Scoring screen. True once this sheet
    // has ever been created server-side OR a create/update is queued
    // locally (see assemble()'s hasStarted below); never downgraded (see
    // apply()) so a background revalidation racing an in-flight create
    // can't flip the form back to the gate screen mid-edit.
    hasStarted: false,
    // #2810 — the last pre-flight verdict that blocked an enqueue, or null.
    // { code, field } mirrors what score-api's own 409 body already carries
    // in data.code/data.field, so a caller can render at the same field name
    // on either side. Never a sync alarm: the local write still lands either
    // way (see persist()/applySheet() below), this is purely informational.
    preflightError: null,
    // #2972 judge round 2 - true once this device's own working sheet is a
    // same-seat CONTENDER (#2815: this create lost the seat's race), never a
    // block on saving - ScoringForm surfaces it as a dismissible notice, the
    // office resolves which sheet wins via scoreadmin (#2816).
    contenderNotice: false,
  });

  // Refs so the debounced save and drain always see the latest sheet + the
  // server id without re-subscribing.
  const serverIdRef = useRef(null);
  const latestSheet = useRef(null);
  // #3008 round 6, driver decision candidate 2 -- a per-sheet generation
  // counter that closes the signPanel/complete/confirmMarks re-seat race
  // rule 2's deepEqual guard could not (see judge-1.md round 5: the stale
  // echo of THIS row's own immediately-preceding ordinary save carries
  // content that genuinely differs from latestSheet.current, because it is
  // missing the signature/completedAt/marksConfirmedAt these functions'
  // own SECOND optimistic mutation just added after persist() returned).
  // Bumped on every in-memory mutation of latestSheet.current (applySheet,
  // applySheetWithReason, and the second optimistic mutation in
  // signPanel/complete/confirmMarks below). persist() reads this
  // SYNCHRONOUSLY at call time (before its `run` is chained onto
  // inflight.current, which executes later, async) and stamps that value
  // onto the enqueued op's payload, so the op always carries the
  // generation of the body it actually sent, not whatever generation
  // happens to be current whenever the outbox drain gets around to it.
  const sheetGenerationRef = useRef(0);
  // #3008 round 6 -- the generation last CONFIRMED to match the server (an
  // op-success emit whose stamped generation equalled sheetGenerationRef at
  // the moment it arrived, or a server-replacement emit accepted under the
  // same condition). Rule 1's server-replacement emit (db.js, no generation
  // on it) re-seats only when this equals sheetGenerationRef.current --
  // otherwise a still-in-flight op's own eventual, generation-matched
  // success is what should reconcile this sheet, not an unrelated
  // ancestor-aware replacement that predates it.
  const lastAckedGenerationRef = useRef(0);
  // Mirrors state.test for the debounced persist() closure below (which
  // can't depend on React state without re-subscribing) — see toBody's
  // scoringModel-conditional percentage handling.
  const testRef = useRef(null);
  // #2810 — mirror state.ride/state.klass the same way testRef mirrors
  // state.test: persist()'s create-branch pre-flight check needs the ride
  // (isRideScratched), and complete()'s confirm/complete pre-flight check
  // needs the class (effectiveTestForClass), from the same debounced/
  // no-React-state closures testRef already serves.
  const rideRef = useRef(null);
  const klassRef = useRef(null);
  // #2810 — the verdict applySheet()'s preflightUpdateScoreSheet found for
  // the CURRENT latestSheet.current, or null. persist() reads this
  // immediately before enqueue so a debounced save skips enqueuing an op it
  // already knows score-api would refuse, without delaying the local commit
  // (scoreSheets.put) that keeps the scribe's typed marks on the tablet.
  const preflightBlockedRef = useRef(null);
  // #2810 AC2 — the sheet's marks as they stood AT marksConfirmedAt (score-
  // common's preflightUpdateScoreSheet freeze baseline), pinned once and kept
  // fixed until a genuinely NEW marksConfirmedAt appears (a reopen/commit
  // re-stamp). applySheet() below always pre-flights the newest edit against
  // THIS baseline, not the previous local edit: judging against the previous
  // edit let a later remarks-only save silently clear a real block, because a
  // remarks-only diff against an ALREADY-blocked (but not yet server-
  // accepted) mark change reads as "no further change" — the blocked mark
  // itself then rode along on the next debounced persist() and 409'd
  // server-side (score-api's own MARKS_CONFIRMED_FROZEN gate) with no
  // pre-flight warning shown first.
  const confirmedMarksRef = useRef(null);
  // #266 CI flake — the `${rideId}::${judgeId}` this load effect last populated
  // latestSheet.current for. Lets a same-sheet re-run of the effect (deps flap:
  // an unstable `api`/`online`, or any re-render) tell itself apart from a real
  // ride switch, so it keeps the scribe's in-session edits instead of clobbering
  // them from a possibly-stale cache (see the load effect's #906 keepSheet note).
  const loadedKey = useRef(null);
  // #3074 -- false once the component using this hook has unmounted.
  // loadedKey above is NOT that signal (it keeps naming the last sheet after
  // unmount), so persist()'s post-await setState calls were still landing on
  // an unmounted component: a silent no-op in a browser, but React reads
  // window.event before it checks the fiber, so after a jsdom teardown the
  // same call threw "window is not defined" as an unhandled rejection.
  const hookMountedRef = useRef(true);
  useEffect(() => {
    hookMountedRef.current = true;
    return () => {
      hookMountedRef.current = false;
    };
  }, []);
  const saveTimer = useRef(null);
  // #3008 judge round 1 (B1) driver decision, rule 3 -- db.js's
  // bulkPutAncestorAware can only see an outbox op, not a typed-but-not-
  // yet-enqueued edit still sitting in the SAVE_DEBOUNCE_MS window. This
  // computes the SAME entityKey format entityKeyOf() uses in db.js
  // (`${effRideId}::${judgeId}`, no `scoreSheet:` prefix) so
  // markUnsavedEdit/clearUnsavedEdit calls below always line up with the
  // row a server-sourced write is about to touch. Mirrors persist()'s own
  // effRideId resolution (manual ride temp id vs. its stamped serverId).
  const unsavedEntityKey = () => {
    if (!judgeId) return null;
    const manual = getManualRideById(rideId);
    const effRideId = manual ? manual.serverId ?? rideId : rideId;
    return `${effRideId}::${judgeId}`;
  };
  // Serializes saves so a debounced create can't fire twice before the first
  // returns the server id (which would create duplicate sheets).
  const inflight = useRef(Promise.resolve());

  useEffect(() => {
    let cancelled = false;
    // #3011 — the sheet generation at the START of this load, so the
    // never-downgrade veto below can tell "the scribe has edited since this
    // load began" (every optimistic edit bumps sheetGenerationRef) from "the
    // form is still showing the untouched blank it cold-started with".
    const generationAtLoad = sheetGenerationRef.current;
    // #627 — judgeId (the scribe's seat) is derived from the ride's class, so it
    // arrives a tick after the ride id. A sheet is keyed by (ride, judge): until
    // the seat is known there is nothing to load and nothing safe to save, so
    // stay in the loading state rather than assembling a judge-less sheet.
    // #2191 — UNLESS the caller has said outright there is no seat coming
    // (allowNoJudge): then a null judgeId falls through to load ride/klass/
    // test anyway, with sheets skipped below exactly as the network/cache
    // fetchers already do for a null judgeId.
    if (!judgeId && !allowNoJudge) {
      setState((s) => ({ ...s, loading: true, error: null }));
      return undefined;
    }

    // #266 CI flake — is this a re-run for the SAME sheet (deps flapped) or a
    // genuine ride switch? On a same-sheet re-run the scribe's local edits in
    // latestSheet.current are authoritative (#906: the scribe is the sole
    // writer once loaded), so BOTH the cache and the api applies below must
    // keep them — not just the api one. Deriving `cleared` in
    // applySheetWithReason isn't enough: apply(cacheCtx) with keepSheet=false
    // re-seats latestSheet.current from a cache that may still hold a
    // just-removed deduction/error (flush updated the server, not the cached
    // sheet doc), silently resurrecting it. On a real ride switch the key
    // differs, so we fall through to taking the fresh cache/api sheet.
    const sheetKey = `${rideId}::${judgeId}`;
    const keepLocal = loadedKey.current === sheetKey && latestSheet.current != null;
    loadedKey.current = sheetKey;

    // #1888 — `online` is a dep of this effect (see the array below) so a
    // reconnect drain re-runs it; that used to ALWAYS flip loading back to
    // true first. ScoringForm has a hard `if (loading) return <spinner>`
    // gate that unmounts the entire scoring form for that window, which
    // resets scroll position and drops form focus (bug #153) and, since the
    // toggle can flap during a real connectivity blip, visibly flashes (bug
    // #154) — see docs comment on the useScoreSheet export / #1888 card
    // investigation. `keepLocal` already distinguishes "same sheet the
    // scribe is looking at, just revalidating" from a genuine new load (a
    // real ride/judge switch, or first mount): only the latter should show
    // the full-screen loading gate. Skip resetting serverIdRef too — on a
    // same-sheet revalidation it's still correct and apply() below refreshes
    // it from whatever the reload finds anyway.
    if (!keepLocal) {
      setState((s) => ({ ...s, loading: true, error: null }));
      serverIdRef.current = null;
    }

    // Build the display context from a set of fetched entities. The sheet base
    // prefers the queued (unsynced) local edits — the outbox op body (#422) —
    // over the server copy, so it's identical whether the parts came from the
    // cache or the network.
    const assemble = async ({ ride, klass, test, apiHorse, apiRider, serverSheet, sheetRideId }) => {
      // Manual rides carry free-text names (no horse/rider ids) — synthesize
      // lightweight objects so ScoringForm's chips render unchanged.
      const horse =
        apiHorse ??
        (ride.isManual && ride.horseName && ride.horseName !== "—"
          ? { id: null, name: ride.horseName }
          : null);
      const rider =
        apiRider ??
        (ride.isManual && ride.riderName && ride.riderName !== "—"
          ? { id: null, firstName: ride.riderName, lastName: "" }
          : null);

      const pendingOp = await getOutboxOp(
        `scoreSheet:${sheetRideId}::${judgeId}`,
      ).catch(() => null);
      const base =
        pendingOp?.payload?.body ??
        serverSheet ?? {
          rideId: sheetRideId,
          judgeId,
          testId: test?.id,
          movementScores: [],
          collectives: {},
          generalRemarks: "",
        };
      const sheet = structuredClone(base);
      sheet.rideId = sheetRideId;
      sheet.judgeId = judgeId;
      if (!sheet.testId) sheet.testId = test?.id;

      // #669 — normalize on read: a sheet saved before "no newlines" was a
      // rule (or written by an older client) may already have a line break
      // in a remarks value. Fix it up here, at the earliest point the sheet
      // enters the app, rather than only guarding new writes.
      if (sheet.generalRemarks) {
        sheet.generalRemarks = normalizeRemarks(sheet.generalRemarks);
      }
      if (sheet.movementScores?.length) {
        sheet.movementScores = sheet.movementScores.map((m) =>
          m.remarks ? { ...m, remarks: normalizeRemarks(m.remarks) } : m,
        );
      }
      if (sheet.collectives) {
        for (const key of Object.keys(sheet.collectives)) {
          const c = sheet.collectives[key];
          if (c?.remarks) {
            sheet.collectives[key] = { ...c, remarks: normalizeRemarks(c.remarks) };
          }
        }
      }

      const judgeName = judgeId ? klass?.judgeAssignments?.[judgeId]?.[0] ?? null : null;
      return {
        ride,
        klass,
        test,
        horse,
        rider,
        judgeName,
        sheet,
        serverSheetId: serverSheet?.id ?? null,
        isCompleted: Boolean(serverSheet?.completedAt),
        // #698 — confirmed once marksConfirmedAt is stamped (server-side,
        // one-way). A completed (signed) sheet was necessarily confirmed
        // first, so treat completedAt as confirmed too (back-compat for
        // sheets signed before #696/#697).
        isConfirmed: Boolean(serverSheet?.marksConfirmedAt || serverSheet?.completedAt),
        // #901 — durable across reopen; see useState init above.
        wasEverSigned: Boolean(serverSheet?.everSignedAt),
        // #2972 judge round 2 - serverSheet here may be THIS device's own
        // contender (see fromCache/fromApi's ownContenderId pick below), not
        // the seat's authoritative sheet. Surface that so ScoringForm can
        // show the notice.
        isContenderSheet: serverSheet ? serverSheet.authoritative === false : false,
        hasPending: Boolean(pendingOp),
        // #2192 — a sheet with a server id has obviously started; one with
        // no server id yet but a queued create/update op (offline, or the
        // create hasn't round-tripped) has also started — that queued op is
        // the local commit (see persist()'s comment on this), so treating
        // it as "not started" would let the gate screen reappear on reload
        // before the server catches up.
        hasStarted: Boolean(serverSheet?.id) || Boolean(pendingOp),
        // #3011 — true when `base` fell through to the synthesized blank
        // sheet: neither a server/cached sheet row nor a queued op existed
        // for this seat, so this context carries NO local content (nothing
        // a scribe ever typed or a server ever returned). The load effect's
        // #906 never-downgrade veto reads this: a blank synthesized sheet
        // must not refuse the real sheet fromApi() is about to fetch.
        synthesizedSheet: !serverSheet && !pendingOp,
      };
    };

    // Apply a context to state. Never downgrade a known server id to null (the
    // create may not have reached the server yet), and keep local edits on a
    // background revalidation (keepSheet) so typing isn't clobbered.
    const apply = (ctx, keepSheet = false) => {
      serverIdRef.current = ctx.serverSheetId ?? serverIdRef.current;
      testRef.current = ctx.test ?? testRef.current;
      rideRef.current = ctx.ride ?? rideRef.current;
      klassRef.current = ctx.klass ?? klassRef.current;
      if (!keepSheet) latestSheet.current = ctx.sheet;
      // #2810 AC2 — adopt the confirmed-marks baseline from whatever this
      // load/revalidation found, regardless of keepSheet: a background
      // revalidation that keeps the scribe's in-progress local edits still
      // needs to know the TRUE server marksConfirmedAt baseline (e.g. an
      // admin's reopen/commit re-stamped it elsewhere). Skipped once the
      // timestamp already matches, so a normal same-sheet re-run is a no-op.
      if (
        ctx.sheet?.marksConfirmedAt &&
        confirmedMarksRef.current?.marksConfirmedAt !== ctx.sheet.marksConfirmedAt
      ) {
        confirmedMarksRef.current = {
          marksConfirmedAt: ctx.sheet.marksConfirmedAt,
          movementScores: ctx.sheet.movementScores,
          collectives: ctx.sheet.collectives,
        };
      }
      setState((s) => ({
        loading: false,
        error: null,
        ride: ctx.ride,
        klass: ctx.klass,
        test: ctx.test,
        horse: ctx.horse,
        rider: ctx.rider,
        judgeName: ctx.judgeName,
        sheet: keepSheet ? latestSheet.current ?? ctx.sheet : ctx.sheet,
        isCompleted: ctx.isCompleted || s.isCompleted,
        // Never downgrade — once confirmed, stays confirmed even if a
        // background revalidation races ahead of a still-queued op.
        isConfirmed: ctx.isConfirmed || s.isConfirmed,
        // #901 — same never-downgrade pattern; server never clears this
        // anyway, but keep it consistent with isCompleted/isConfirmed.
        wasEverSigned: ctx.wasEverSigned || s.wasEverSigned,
        saveError: s.saveError,
        // #2192 — never-downgrade, same reasoning as isCompleted/isConfirmed
        // above.
        hasStarted: ctx.hasStarted || s.hasStarted,
        // #2972 judge round 2 - live, NOT never-downgrade: a scoreadmin
        // switch_authoritative_score_sheet (#2816) can resolve the contender
        // in either direction, and the notice should track the sheet's
        // CURRENT status, not stick once shown.
        contenderNotice: Boolean(ctx.isContenderSheet),
      }));
    };

    // #2972 judge round 2 - this device's own sheet, once a create has
    // resolved, is whichever row `ownContenderId` names (stamped onto the
    // provisional entityKey row by sync.js's markProvisionalSheetAsContender
    // once a lost create's race lands) if one exists, even though it is not
    // the seat's authoritative sheet - falling back to the ordinary
    // authoritative pick otherwise. Shared by fromCache/fromApi below so both
    // read paths agree on which row is "mine".
    // #2972 judge round 3 - once this device owns a contender it NEVER falls
    // through to the seat's authoritative (the winner's) sheet: that would
    // show this scribe the winner's marks and seat serverIdRef on the
    // winner's id (apply()), so the next autosave overwrites the winner.
    // When the contender's own server row is not in `sheets`, the stamped
    // provisional row (this scribe's typed text) stands in for it, under the
    // contender's id.
    const pickOwnServerSheet = (sheets, provisionalRow) => {
      const ownContenderId = provisionalRow?.contenderServerId ?? null;
      if (ownContenderId) {
        const own = sheets.find((s) => s.id === ownContenderId && !s.provisional);
        if (own) return own;
        // eslint-disable-next-line no-unused-vars
        const { provisional, contenderServerId, ...typed } = provisionalRow;
        return { ...typed, id: ownContenderId, authoritative: false };
      }
      return (
        sheets.find(
          (s) => s.judgeId === judgeId && !s.provisional && isAuthoritativeScoreSheet(s),
        ) ?? null
      );
    };

    // #203 / #380 — an emergency manual ride isn't on the day sheet, so it's
    // read from the local mirror instead of GET /ride.
    const fromCache = async () => {
      const manual = getManualRideById(rideId);
      const ride = manual ?? preloadedRide ?? (await ridesCache.get(rideId)) ?? null;
      if (!ride) return null;
      const sheetRideId = manual ? manual.serverId ?? rideId : rideId;
      const klass =
        preloadedKlass ??
        (ride.classId ? (await classesCache.get(ride.classId)) ?? null : null);
      // #462 — ride.testId is the ONLY source. Never fall back to klass.testId:
      // in a Test-of-Choice class the class test is meaningless, so substituting
      // it would silently score a ride against the wrong test. A missing testId
      // must fail loud (see ScoringForm's "no test assigned" branch), not score.
      const testId = ride.testId;
      const test = preloadedTest ?? (testId ? (await testsCache.get(testId)) ?? null : null);
      const apiHorse = ride.horseId ? (await horsesCache.get(ride.horseId)) ?? null : null;
      const apiRider = ride.riderId ? (await peopleCache.get(ride.riderId)) ?? null : null;
      const sheets = judgeId ? await scoreSheets.byRideJudge(sheetRideId, judgeId) : [];
      // #2756 — the provisional entityKey-keyed row persist() writes for an
      // unsynced create is cache content, NOT a server sheet: adopting it
      // here would seat serverIdRef with `rideId::judgeId` and stamp
      // isCompleted/isConfirmed off a row that has neither. The typed text it
      // carries still reaches the form through assemble()'s preferred base —
      // the queued op's own payload — exactly as before.
      // #2972 - an arbitrary cached contender for this seat must never be
      // adopted as this scribe's working sheet (isAuthoritativeScoreSheet,
      // score-common/utils/score-sheet-authority.js) - but THIS device's OWN
      // contender (pickOwnServerSheet's ownContenderId branch) is exactly
      // this scribe's working sheet, authoritative or not.
      const entityKey = judgeId ? `${sheetRideId}::${judgeId}` : null;
      const provisionalRow = entityKey
        ? await scoreSheets.get(entityKey).catch(() => null)
        : null;
      const serverSheet = pickOwnServerSheet(sheets, provisionalRow);
      return assemble({ ride, klass, test, apiHorse, apiRider, serverSheet, sheetRideId });
    };

    const fromApi = async () => {
      const manual = getManualRideById(rideId);
      const ride = manual ?? preloadedRide ?? unwrap(await api.rides.getRide(rideId));
      const sheetRideId = manual ? manual.serverId ?? rideId : rideId;
      const klass =
        preloadedKlass ??
        (ride.classId
          ? await api.classes.getClass(ride.classId).then(unwrap).catch(() => null)
          : null);
      // #462 — ride.testId only (see fromCache); no class-test fallback.
      const testId = ride.testId;
      const [test, apiHorse, apiRider] = await Promise.all([
        preloadedTest
          ? Promise.resolve(preloadedTest)
          : testId
          ? api.tests.getTest(testId).then(unwrap).catch(() => null)
          : null,
        ride.horseId
          ? api.horses.getHorse(ride.horseId).then(unwrap).catch(() => null)
          : null,
        ride.riderId
          ? api.people.getPerson(ride.riderId).then(unwrap).catch(() => null)
          : null,
      ]);
      // score-api's listScoreSheets queries the rideId GSI and does NOT also
      // filter by judgeId, so it returns every judge's sheet — pick this one's.
      const sheets = judgeId
        ? await api.scoreSheets
            .listScoreSheets(sheetRideId, judgeId)
            .then(unwrap)
            .catch(() => [])
        : [];
      // #2972 - same own-contender-or-authoritative pick as fromCache above:
      // the server may return a same-seat contender too, and it may be this
      // device's own (pickOwnServerSheet handles both).
      const provisionalRowForApi = judgeId
        ? await scoreSheets.get(`${sheetRideId}::${judgeId}`).catch(() => null)
        : null;
      const serverSheet = pickOwnServerSheet(sheets, provisionalRowForApi);

      // Write-through to the cache so this ride opens offline next time. Skip
      // when the caller preloaded the entity (#1781): it already came from
      // this caller's own cache-writing fetch (ClassGrid's useRidesForClass /
      // useClassGridTest), and a preloaded `ride` here may be denormalized
      // (status/percentage merged in by useRidesForClass) — writing that back
      // would pollute the raw ride cache other readers rely on.
      if (!ride.isManual && !preloadedRide) await ridesCache.put(ride);
      if (klass && !preloadedKlass) await classesCache.put(klass);
      if (test && !preloadedTest) await testsCache.put(test);
      if (apiHorse) await horsesCache.put(apiHorse);
      if (apiRider) await peopleCache.put(apiRider);
      // #2726 — a queued (pending/error/terminal) op for THIS sheet means
      // persist()'s own optimistic write (above) already advanced the local
      // row past whatever this GET just fetched. A plain putAll of every
      // judge's sheet here — this background revalidation reruns on every
      // mount/reconnect while online, not just a cold load — would clobber
      // that row right back to the server's pre-edit snapshot, which is
      // exactly how a refused save's typed text ended up surviving nowhere
      // but the outbox payload (#2726 root cause). Only THIS judge's row
      // needs protecting; every other judge's sheet on this ride still
      // writes straight through as before.
      const pendingOp = judgeId
        ? await getOutboxOp(`scoreSheet:${sheetRideId}::${judgeId}`).catch(
            () => null,
          )
        : null;
      const sheetsToCache = pendingOp
        ? sheets.filter((s) => s.judgeId !== judgeId)
        : sheets;
      // #3008 -- route through putAllFromServer, not a raw putAll, so this
      // write stamps `_syncedSheet` (the tracked common-ancestor field
      // sync.js's stale_write merge and toBody()'s baseVersion both read)
      // on every sheet EXCEPT one with its own pending edit, whose ancestor
      // is preserved instead of advanced (db.js's bulkPutAncestorAware).
      // Without stamping at all, a sheet's FIRST save after a fresh
      // load/reload has no ancestor row yet, so toBody() omits baseVersion
      // and the save skips the server's stale check entirely (back-compat
      // skip, not a crash) -- exactly the scribe-opens-on-a-second-device-
      // and-saves-once case this card's AC1 is meant to cover. The SECOND
      // edit onward already worked before this fix, once the first save's
      // own success branch stamped the row. sheetsToCache above already
      // excludes THIS judge's own row entirely when it has a pending op
      // (so its live edit isn't clobbered here at all, #2726); putAllFromServer
      // additionally protects every OTHER judge's row on the same ride that
      // may itself have a pending edit (panel scoring, #3008 HANDOFF 4).
      if (sheetsToCache.length) await scoreSheets.putAllFromServer(sheetsToCache);

      return assemble({ ride, klass, test, apiHorse, apiRider, serverSheet, sheetRideId });
    };

    (async () => {
      let cacheCtx = null;
      try {
        cacheCtx = await fromCache();
      } catch {
        cacheCtx = null;
      }
      if (!cancelled && cacheCtx) apply(cacheCtx, keepLocal);

      if (online) {
        try {
          const apiCtx = await fromApi();
          // #906 — never-downgrade, extended to sheet CONTENT: once a sheet has
          // been shown locally (cacheCtx, however it got there), a background
          // revalidation must never overwrite marks/collectives/annotations
          // with whatever fromApi() just fetched. This used to be gated on
          // `apiCtx.hasPending` (an outbox op still queued) alone, on the
          // assumption that once a save flushed there was nothing left to
          // protect — but a flush racing a slower in-flight GET (or a GET
          // response reflecting a pre-edit snapshot) can land AFTER the outbox
          // has already drained, clobbering just-typed marks with stale
          // server data (#906). The scribe is the sheet's only writer once
          // loaded, so keeping latestSheet.current here is always safe; only
          // a truly cold load (no cacheCtx at all — or, per #3011 below, a
          // synthesized-blank cacheCtx the scribe hasn't touched) takes
          // fromApi()'s sheet.
          // #3011 — one narrow hole in the #906 veto: a cacheCtx assembled
          // from the SYNTHESIZED blank base (fromCache found the ride — e.g.
          // a class-list prefetch or the old module-level warm cache had
          // already written it — but neither a sheet row nor a queued op for
          // this seat) holds NO local content to protect, so it must not
          // refuse the real sheet fromApi() just fetched. Without this, the
          // blank stayed seated for the whole mount whenever the
          // sheet-content-replaced rescue (the B1-rule-2 subscription below)
          // was gated off — a stuck unsaved-edit mark, or db.js's
          // bulkPutAncestorAware skipping the diverged row's write (and thus
          // its emit) — which is exactly how a re-opened ride lost its
          // seeded word-group annotations (card 3011, observed on the #2980/
          // #2991 ports). A scribe edit since this load began bumps the
          // generation and restores the full veto: their typed marks stay
          // protected exactly as #906 intended.
          const scribeEditedSinceLoad =
            sheetGenerationRef.current !== generationAtLoad;
          if (!cancelled)
            apply(
              apiCtx,
              keepLocal ||
                (Boolean(cacheCtx) &&
                  (!cacheCtx.synthesizedSheet || scribeEditedSinceLoad)),
            );
        } catch (err) {
          if (!cancelled && !cacheCtx) {
            setState({
              loading: false,
              error: err,
              ride: null,
              klass: null,
              test: null,
              horse: null,
              rider: null,
              judgeName: null,
              sheet: null,
              isCompleted: false,
            });
          }
        }
      } else if (!cancelled && !cacheCtx) {
        // Offline with nothing cached — settle out of the loading state.
        setState((s) => ({ ...s, loading: false }));
      }
    })();

    return () => {
      cancelled = true;
      // #3008 round 10 -- the driver's own finding: cancelling a still-
      // pending debounced save here (the old behavior, `clearTimeout` alone)
      // silently DROPS the scribe's typed-but-not-yet-enqueued edit whenever
      // this hook unmounts/re-runs inside the SAVE_DEBOUNCE_MS window (a real
      // ride switch, or the scribe leaving the sheet). Worse, it leaves
      // markUnsavedEdit's mark for this entityKey stuck forever -- nothing
      // else ever clears it (only persist()'s own enqueue does, and that
      // never ran) -- which makes db.js's bulkPutAncestorAware treat this row
      // as permanently diverged and skip caching the server row for it ever
      // again. A later save then has no cached row to resolve serverIdRef
      // from and wrongly issues a createScoreSheet for a sheet that already
      // exists server-side (see persist()'s own #3008 round 10 comment on
      // the belt-and-braces fallback for the same failure mode).
      //
      // Flush instead of dropping: fire persist() with whatever
      // latestSheet.current holds right now (fire-and-forget -- a cleanup
      // function can't await a promise; inflight.current already serializes
      // this against any other in-flight persist() call for this entityKey).
      // persist() itself clears the unsaved mark once the edit reaches the
      // durable outbox, so no separate clear call is needed on this branch.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
        // #3008 round 11 -- registered synchronously (before any await),
        // so a test harness awaiting _waitForPendingUnmountFlushesForTests()
        // right after this unmount is guaranteed to see it: this whole
        // cleanup function runs synchronously start to finish, so the entry
        // exists in the set before this cleanup call returns, well before
        // any later beforeEach/afterEach gets to run.
        // #3008 judge round 2, BLOCKER 1 -- snapshot this sheet's refs NOW
        // (see persist()'s `detached` comment): the next effect run resets
        // serverIdRef before this flush's async body ever executes.
        const flush = persist(latestSheet.current, undefined, {
          serverId: serverIdRef.current,
          test: testRef.current,
          ride: rideRef.current,
          preflight: preflightBlockedRef.current,
        }).catch(() => {});
        pendingUnmountFlushes.add(flush);
        flush.finally(() => pendingUnmountFlushes.delete(flush));
      } else {
        // Nothing was pending -- any earlier persist() already cleared the
        // mark through its own normal path. Clear it again anyway, purely
        // defensively, so no path can leave it stuck with no debounced save
        // left around to ever flush it.
        clearUnsavedEdit(unsavedEntityKey());
      }
    };
  }, [api, rideId, judgeId, online, allowNoJudge]);

  // #3008 judge round 1 (B1), driver decision rule 2 -- re-seat this hook's
  // in-memory sheet when this sheet's IndexedDB content is REPLACED out from
  // under it: db.js's bulkPutAncestorAware writing a server row through (rule
  // 1's safe, no-divergence branch), or sync.js's applyOp landing a
  // successful updateScoreSheet -- the same call site a merge-resubmit's
  // merged body lands through. Subscribed by the SAME entityKey format
  // unsavedEntityKey() computes (`${effRideId}::${judgeId}`), so this only
  // ever hears about the sheet actually mounted.
  //
  // Never re-seats while hasUnsavedEdit() is true for this sheet -- a typed-
  // but-not-yet-enqueued edit (the SAVE_DEBOUNCE_MS window) or a queued op
  // still in flight means the scribe's own edit is the one in progress; a
  // stale_write 409 (if any) and its own auto-merge is what reconciles that
  // case, not this subscription clobbering it out from under them.
  //
  // #3008 round 4 fix -- sync.js's applyOp emits this UNCONDITIONALLY on
  // every successful updateScoreSheet, ordinary saves included (see its own
  // comment: "on an ordinary (non-merge) save this is a no-op in practice").
  // That "no-op" claim was about content, not about renders: without this
  // deepEqual guard, an ordinary save re-seated latestSheet.current + called
  // setState with a brand-new (but content-identical) object on literally
  // every save, forcing an extra render on every keystroke's debounced save.
  // ClassCeremony's panel-signature/Complete-gating logic and ClassGrid's
  // sync-pending derivation both re-derive from hook output on each render,
  // and that extra render was landing between their own state transitions
  // (e.g. a re-render right after a reconnect flush lands, but before other
  // per-row effects settle). Skipping the re-seat entirely when the incoming
  // content deep-equals what's already seated makes the merge-resubmit path
  // (where content genuinely differs) unaffected while restoring ordinary
  // saves to their previous no-render behavior.
  //
  // #3008 round 6, driver decision candidate 2 -- an op-success emit (sync.js's
  // applyOp("updateScoreSheet"), ordinary save or merge-resubmit alike) now
  // carries the generation of the body IT sent as a 2nd callback arg; db.js's
  // rule-1 server-replacement emit carries none (undefined), same as before
  // this round. The two are gated differently: an op-success emit only
  // re-seats when nothing has mutated latestSheet.current since that body was
  // captured (its generation === this hook's CURRENT generation) -- this is
  // what closes the signPanel/complete/confirmMarks race (round 5's root
  // cause): their own second optimistic mutation bumps the generation past
  // what the preceding persist() call's op carried, so that op's later,
  // stale-relative-to-this-row echo is skipped instead of clobbering the
  // signature/completedAt/marksConfirmedAt it just added. A server-
  // replacement emit still requires hasUnsavedEdit() false (as before), AND
  // additionally that nothing is unaccounted for generation-wise (current
  // generation === lastAckedGenerationRef) -- a still in-flight op's own
  // eventual, generation-matched success is what should reconcile this
  // sheet, not an ancestor-aware replacement that may predate it.
  useEffect(() => {
    const entityKey = unsavedEntityKey();
    if (!entityKey) return undefined;
    return subscribeToSheetContentReplaced(entityKey, (content, generation) => {
      if (generation !== undefined) {
        if (generation !== sheetGenerationRef.current) return;
      } else {
        if (hasUnsavedEdit(entityKey)) return;
        if (sheetGenerationRef.current !== lastAckedGenerationRef.current) return;
      }
      lastAckedGenerationRef.current = sheetGenerationRef.current;
      const next = { ...content };
      if (next.generalRemarks) next.generalRemarks = normalizeRemarks(next.generalRemarks);
      if (next.movementScores?.length) {
        next.movementScores = next.movementScores.map((m) =>
          m.remarks ? { ...m, remarks: normalizeRemarks(m.remarks) } : m,
        );
      }
      if (deepEqual(next, latestSheet.current)) return;
      latestSheet.current = next;
      setState((s) => (s.sheet ? { ...s, sheet: next } : s));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideId, judgeId]);

  // Persist the whole sheet through the local-first outbox (#422): every save
  // enqueues the entire sheet (upsert per ride+judge, last-write-wins) and,
  // when online, flushes immediately (write-through) so scores reach the cloud
  // in near-real-time. Nothing is lost regardless of connectivity — offline the
  // op simply waits in the outbox for the reconnect drain.
  const persist = useCallback(
    (sheet, generationOverride, detached = null) => {
      // No seat, no sheet key (#627) — refuse rather than write a judge-less sheet.
      if (!sheet || !judgeId) return inflight.current;
      // #3008 round 6 -- captured SYNCHRONOUSLY here, at persist()'s own call
      // time, not inside `run` below: `run` is chained onto inflight.current
      // (`.then(run, run)`), so it executes later, async, by which point a
      // caller's own second optimistic mutation (signPanel/complete/
      // confirmMarks) may already have bumped sheetGenerationRef past the
      // generation `sheet` (this exact argument) was actually built from.
      // Reading it here instead ties the stamped generation to the body this
      // call is actually going to send.
      //
      // #3008 round 7 -- `generationOverride` closes a SECOND race candidate
      // 2 left open (judge-1.md round 6 handoff, AC4 -- two panel judges
      // signing the SAME row in sequence). signPanel/complete/confirmMarks
      // all call persist() first (to flush any pending edit) and THEN make
      // their own second optimistic mutation -- but there is an `await
      // enqueue(...)` between the two (their own op's enqueue), which yields
      // to the event loop. If persist()'s own flush op's success echo lands
      // during exactly that gap -- generation captured here, mutation not
      // yet applied -- sheetGenerationRef.current still reads as the SAME
      // value this call captured, the echo's generation matches, and the
      // subscription re-seats latestSheet.current from the echo's content
      // BEFORE the caller's own mutation runs. A PUT echo never carries
      // signatures/completedAt/marksConfirmedAt (those are server-managed,
      // stripped from the update body), so that re-seat silently drops
      // whatever this row already carried from an EARLIER such call (e.g.
      // the first judge's signature), and the caller's own mutation then
      // merges its own change onto that now-emptied base, permanently
      // losing the earlier one. Measured on row 9 in the failing AC4 test:
      // judge-a's signature vanished the instant judge-b signed, because
      // judge-b's own flush echoed back (generation matched, gap hit)
      // between judge-b's persist() and judge-b's own signatures merge.
      // Passing the caller's PRE-bump generation explicitly (instead of
      // reading sheetGenerationRef.current here) lets the caller bump the
      // counter BEFORE calling persist() at all, so it is already advanced
      // for the ENTIRE async window this function's own body runs in --
      // closing the gap instead of racing it. Ordinary callers (applySheet's
      // scheduleSave, with no second mutation coming) omit this and keep
      // reading sheetGenerationRef.current at call time, unchanged.
      const opGeneration =
        generationOverride !== undefined ? generationOverride : sheetGenerationRef.current;
      // #3020 -- captured SYNCHRONOUSLY here, at persist()'s own call time,
      // for the same reason opGeneration is (comment above): these three
      // refs belong to the sheet THIS call was made for and never
      // legitimately change for it, but `run` below executes async and a
      // ride switch mid-await re-seats testRef/rideRef/preflightBlockedRef
      // for whatever ride is mounted NOW. A straddling (non-detached) save
      // that read them inside `run` was picking up ride-2's test/ride/
      // preflight verdict for a body built from ride-1's sheet. A detached
      // (#3008 unmount) flush already carries its own call-time snapshot
      // (detached.test/detached.ride/detached.preflight) and ignores these.
      // callTimeServerId is the same idea applied to the server id: a
      // straddling save's ride may already have had one resolved (from an
      // earlier save/load) even though this run, once unmounted, can no
      // longer trust serverIdRef.current -- carrying it forward as the
      // detached-path fallback below means a cache miss at run time still
      // resolves to an update, never a second createScoreSheet for a sheet
      // that already exists (AC3).
      const callTimeTest = testRef.current;
      const callTimeRide = rideRef.current;
      const callTimePreflightBlocked = preflightBlockedRef.current;
      const callTimeServerId = serverIdRef.current;
      // #3008 judge round 2, BLOCKER 1 -- `detached` is the load effect's
      // unmount/re-run flush: a snapshot of this sheet's refs taken
      // SYNCHRONOUSLY in that cleanup, before the next effect run (a ride
      // switch) resets or re-seats them. `run` below executes async, after
      // that next run, so a detached flush must read ONLY its snapshot and
      // must NEVER write serverIdRef (or this hook's state): those belong to
      // whatever sheet is mounted NOW. Seating the previous ride's sheet id
      // there made the next rider's first save an updateScoreSheet against
      // the previous rider's sheet. Server-id resolution for a detached
      // flush lands in a local instead.
      const hookSheetKey = `${rideId}::${judgeId}`;
      const isMountedSheet = () => loadedKey.current === hookSheetKey;
      // #3019 -- a NORMAL (non-detached) persist() call's own pre-enqueue
      // server-id resolution just below (the queuedOp/cached lookups) awaits
      // real async work (getOutboxOp, scoreSheets.byRideJudge,
      // resolveOwnCachedServerRow) -- a ride switch can land in the middle of
      // that await just as easily as inside the unmount-triggered detached
      // flush above, and when it does, this run is no longer for the mounted
      // sheet: it must stop reading and writing the shared serverIdRef right
      // then, exactly like a detached flush already does, or ride-1's own
      // resolution can seat (or use) ride-2's server id once ride-2 is the
      // one mounted. isMountedSheet() is re-checked on every read/write
      // below (not decided once up front) because the switch can land
      // between any of this function's several awaits, not only before the
      // first one.
      // #3020 -- a straddling normal (non-detached) call used to seed this
      // with a bare null once unmounted, discarding a server id the ride
      // genuinely had at call time (callTimeServerId) and forcing a
      // re-resolve from cache/outbox that can miss (AC3). Falling back to
      // callTimeServerId here means an unmounted straddling save with a
      // known id skips that re-resolution entirely and enqueues an update.
      let detachedServerId = detached ? detached.serverId ?? null : callTimeServerId ?? null;
      const currentServerId = () =>
        detached || !isMountedSheet() ? detachedServerId : serverIdRef.current;
      const seatServerId = (id) => {
        if (detached || !isMountedSheet()) detachedServerId = id;
        else serverIdRef.current = id;
      };
      const run = async () => {
        // A manual ride's server id may have been stamped since the last save;
        // until it is, the op carries the temp id so the engine remaps it once
        // the ride syncs (rather than POSTing a dangling ride reference).
        const manual = getManualRideById(rideId);
        const effRideId = manual ? manual.serverId ?? rideId : rideId;
        const isTempRide = Boolean(manual && !manual.serverId);
        const entityKey = `${effRideId}::${judgeId}`;

        // Resolve the server sheet id: from load, or the id the engine cached
        // once a create drained.
        // #2897 — a save that races the in-flight FIRST create still resolves
        // null here, and that is fine: it enqueues a createScoreSheet under
        // the SAME opId (`scoreSheet:${entityKey}`), which db.js's enqueue
        // upserts into the single queued op, and the create→update switch is
        // ENGINE-SIDE — sync.js's applyOp promotes the re-enqueued (newer-
        // body) op to an updateScoreSheet against the created/adopted server
        // id once the create round trip lands (promoteQueuedCreateToUpdate,
        // pinned in sync.test.js), so only one create ever reaches the server
        // and this save's marks are not dropped. The LOCAL write must NEVER
        // wait on that round trip: scribe-fe's axios has no timeout, a flush
        // can hang forever on flaky venue wifi (#515 moved flush off the save
        // path for exactly this reason), and saves are serialised — an await
        // here would hang every later save plus Complete/Confirm/Sign, which
        // all `await persist`, leaving typed marks in React state only.
        // #3008 round 10 -- populated below, only when actually needed
        // (serverIdRef unresolved coming in), and reused for two purposes:
        // (a) the belt-and-braces fallback right below, and (b) the
        // createKey-reuse read this block already did on its own further
        // down (moved up rather than fetched twice). Left null when
        // serverIdRef.current is already known -- neither purpose applies
        // then, and this read is otherwise on every single debounced save.
        let queuedOp = null;
        if (!currentServerId()) {
          queuedOp = await getOutboxOp(`scoreSheet:${entityKey}`).catch(
            () => null,
          );
          const cached = await scoreSheets
            .byRideJudge(effRideId, judgeId)
            .catch(() => []);
          // #2756 — skip the provisional entityKey-keyed row this same
          // persist() writes below: its id is `rideId::judgeId`, not a server
          // id, and adopting it here would enqueue an updateScoreSheet
          // against a key score-api has never heard of.
          // #2972 - and skip an ARBITRARY cached contender: seating
          // serverIdRef on some other seat-holder's contender would enqueue
          // this scribe's future saves as updates against a sheet no reader
          // (including this scribe's own reload) ever shows.
          // #2972 judge round 3 (driver rule) - but THIS device's OWN
          // contender comes first, before the seat's authoritative row: once
          // this device lost the create race, the winner's authoritative
          // sheet is usually cached too, and adopting it would turn this
          // save into an update that overwrites the winner. Adopting the
          // contender also means a later save never re-POSTs a create (a
          // fresh random createKey every retry would only mint another
          // contender, never converge). See resolveOwnCachedServerRow.
          const { id: ownServerId } = await resolveOwnCachedServerRow(
            cached,
            entityKey,
            judgeId,
          );
          if (ownServerId) {
            seatServerId(ownServerId);
          } else if (
            queuedOp?.type === "updateScoreSheet" &&
            queuedOp.payload?.serverId
          ) {
            // #3008 round 10 -- belt-and-braces: the cache above can come up
            // completely empty for this seat even though the sheet genuinely
            // exists server-side (e.g. db.js's bulkPutAncestorAware skipping
            // the cache write while a row was wrongly, or is even correctly,
            // treated as diverged -- see this file's own #3008 comment on
            // that subscription, and the driver note this round fixes). An
            // update ALREADY queued for this exact entityKey is proof the
            // sheet exists server-side (its own serverId is real, never a
            // provisional/temp value -- only a genuine createScoreSheet
            // success ever seeds one). Adopting it here is what stops a
            // later save from wrongly issuing a SECOND createScoreSheet for
            // a sheet that already exists.
            seatServerId(queuedOp.payload.serverId);
          }
        }
        const serverId = currentServerId();

        // #2810 — pre-flight before enqueue. A create checks isRideScratched
        // against rideRef (mirrored by apply() the same way testRef is); an
        // update reads the verdict applySheet() already computed for THIS
        // exact sheet transition (preflightBlockedRef) — recomputing it here
        // would need the sheet as it stood BEFORE this edit, which persist()
        // (running off a debounce timer) never has, only applySheet() does.
        // Either way, a blocked verdict skips the enqueue — never send an op
        // score-api will provably refuse — but the local commit just below
        // still runs unconditionally: pre-flight never blocks the local save
        // (AC4).
        // #3020 -- reads callTimePreflightBlocked/callTimeRide (captured
        // synchronously at persist()'s own call time, above) instead of the
        // live refs: those belong to whatever ride is mounted BY THE TIME
        // this async body runs, which a straddling save's own ride no
        // longer is.
        const preflightVerdict = serverId
          ? detached
            ? detached.preflight
            : callTimePreflightBlocked
          : (() => {
              const verdict = preflightCreateScoreSheet({
                ride: detached ? detached.ride : callTimeRide,
              });
              return verdict.ok ? null : verdict;
            })();

        // #3008 -- read this row's tracked ancestor (_syncedSheet) BEFORE
        // building the body, so an update's baseVersion matches what this
        // edit actually started from. Reused below instead of a second read.
        const ancestorRow = serverId
          ? await scoreSheets.get(serverId).catch(() => null)
          : null;
        // #3020 -- callTimeTest, not testRef.current: see the comment above
        // callTimeTest's capture, at the top of persist().
        const body = {
          ...toBody(sheet, detached ? detached.test : callTimeTest, ancestorRow?._syncedSheet),
          rideId: effRideId,
        };
        // #2972 judge round 2 - createKey must be a random UUID minted ONCE
        // per logical create ATTEMPT (this device's own, this DB's own), not
        // derived from rideId+judgeId: score-api's deriveCreateKeySheetId
        // (scoresheet.js) hashes (orgId, rideId, judgeId, createKey) into the
        // sheet id, so a seat-derived key made every device's/every wiped-
        // DB's FIRST create for the same seat land on the SAME id - a second
        // scribe device would silently overwrite the first device's sheet
        // instead of ever becoming a #2815 contender (the exact defect this
        // round fixes). Reuse across a re-enqueue/retry of the SAME create
        // still matters (so a flaky-connection replay lands on the id it
        // already won rather than racing a fresh contender into existence),
        // so the key is read back off this op's own queued outbox entry
        // (`queuedOp`, read once above) if one is already in flight for this
        // create, and only minted fresh when there is none yet. Only
        // meaningful on a create - an update carries a real serverId already
        // and score-api ignores createKey there.
        if (!serverId) {
          const reusableKey =
            queuedOp?.type === "createScoreSheet" && typeof queuedOp.payload?.body?.createKey === "string"
              ? queuedOp.payload.body.createKey
              : null;
          body.createKey = reusableKey ?? crypto.randomUUID();
        }

        if (preflightVerdict) {
          if (isMountedSheet() && hookMountedRef.current) setState((s) => ({ ...s, preflightError: preflightVerdict }));
        } else {
          await enqueue({
            opId: `scoreSheet:${entityKey}`,
            type: serverId ? "updateScoreSheet" : "createScoreSheet",
            entityKey,
            tempId: isTempRide ? effRideId : null,
            // #3008 round 6 -- the generation of the body this op is
            // actually sending (see opGeneration's own comment above).
            payload: {
              rideId: effRideId,
              judgeId,
              serverId: serverId ?? null,
              body,
              generation: opGeneration,
            },
          });
          if (isMountedSheet() && hookMountedRef.current) {
            setState((s) => (s.preflightError ? { ...s, preflightError: null } : s));
          }
          // #3008 rule 3 -- the edit is now durable in the outbox, which
          // db.js's bulkPutAncestorAware already checks (pendingOp) -- the
          // unsaved-edit flag's ONLY job was covering the gap before this
          // enqueue. A preflight-blocked save (the branch above, no enqueue)
          // deliberately does NOT reach here, so the flag stays set and the
          // edit stays protected until a later save actually enqueues.
          clearUnsavedEdit(entityKey);
        }

        // #2726 — the outbox enqueue above is durable, but it is not the
        // local scoreSheets cache row: applyOp("updateScoreSheet") only
        // writes that row on a SUCCESSFUL response (sync.js), so a save that
        // is later REFUSED (409, terminal — no more retries, ever) left the
        // typed text nowhere the tablet's own scoreSheets row could show it,
        // only inside the stuck outbox payload (measured on a real tablet
        // export, card #2726). Merge the just-typed body into the cached row
        // right now, same-turn as the local commit above, so it survives
        // regardless of what the network round trip does. Only once a
        // server id is already known: an unsynced create has no row keyed on
        // anything yet — createScoreSheet's own success path is what creates
        // it (sync.js applyOp). Spread order matters: existingRow first so
        // SERVER_MANAGED fields toBody() stripped out of body (id, orgId,
        // createdAt, totalPoints, …) survive the merge untouched.
        // #2756 closes #2726's documented scope limit: with no serverId yet,
        // write a PROVISIONAL row keyed on the same client-side entityKey
        // (`rideId::judgeId`) the outbox op already carries, flagged
        // `provisional: true` so every server-id resolution (persist() above,
        // the post-flush reconcile below, sync.js's complete/confirm/panelSign,
        // fromCache()) skips it. If this sheet's own createScoreSheet is later
        // refused (terminal), the typed text survives here, not only inside
        // the stuck outbox payload that discardOp destroys. On success,
        // sync.js applyOp rekeys: it caches the server row and removes this
        // provisional one, leaving no duplicate (sync.js, #2756).
        if (serverId) {
          // #3008 -- reuse the ancestor read above instead of a second get().
          await scoreSheets
            .put({ ...(ancestorRow ?? {}), ...body, id: serverId })
            .catch(() => {});
        } else {
          const existingRow = await scoreSheets.get(entityKey).catch(() => null);
          await scoreSheets
            .put({
              ...(existingRow ?? {}),
              ...body,
              id: entityKey,
              rideId: effRideId,
              judgeId,
              provisional: true,
            })
            .catch(() => {});
        }

        // #515 — the enqueue above is the local commit: the write is already
        // durable in the outbox, which is what a caller's button spinner
        // should key off (a PWA optimistic save "completes" the moment it's
        // queued, same contract completeScoreSheet already documents for the
        // fully-offline case below). flush() is a network round-trip that
        // can be slow or hang on flaky venue wifi even while
        // `navigator.onLine` says true — awaiting it here would hold the
        // Save/Confirm/Sign button's spinner hostage to that latency. Fire it
        // in the background instead; reconcile serverIdRef and surface a
        // post-flush error once it lands, but don't block on it.
        if (syncEngine.isOnline()) {
          syncEngine
            .flush()
            .then(async () => {
              const after = await scoreSheets
                .byRideJudge(effRideId, judgeId)
                .catch(() => []);
              // #2756 — same provisional-row skip as persist()'s pre-enqueue
              // resolution: only a real server row may seat serverIdRef or
              // feed the SERVER_ONLY_FIELDS merge below.
              // #2972 AC3 - and never a stranger's cached contender.
              // #2972 judge round 3 (driver rule) - THIS device's own
              // contender (its create lost the seat's race: a 201 with
              // `authoritative: false`, stamped onto the provisional row by
              // sync.js) resolves FIRST, before the seat's authoritative row.
              // An authoritative-first pick here re-seated serverIdRef on the
              // WINNER's sheet after every synced save, so the next autosave
              // was an updateScoreSheet that overwrote the winner with this
              // scribe's whole sheet.
              const { id: ownServerId, row: serverRow } =
                await resolveOwnCachedServerRow(after, entityKey, judgeId);
              // #3008 judge round 2, BLOCKER 1 -- this background flush can
              // resolve after the scribe switched rides: only seat the id
              // while THIS persist()'s sheet is still the mounted one, and
              // never from a detached (unmount/re-run) flush at all.
              if (ownServerId && !detached && isMountedSheet()) {
                serverIdRef.current = ownServerId;
              }
              // #2472 — the cached row `serverRow` is whatever sync.js's
              // applyOp just wrote (created/updated from score-api), which is
              // the ONLY place this sheet's server-stamped numericOnly /
              // noSignatureNeeded ever reach the client for a sheet that
              // started this session with neither key. Merge just those two
              // fields into the sheet still mounted — never the whole row,
              // that would be exactly the #906 regression (background data
              // clobbering just-typed marks). Guarded on rideId/judgeId so a
              // scribe who has already switched rides by the time this
              // background flush resolves never has a stale merge land on
              // whatever sheet is mounted now.
              const cur = latestSheet.current;
              if (
                serverRow &&
                cur &&
                cur.rideId === effRideId &&
                cur.judgeId === judgeId
              ) {
                const patch = {};
                let changed = false;
                for (const key of SERVER_ONLY_FIELDS) {
                  if (serverRow[key] !== undefined && cur[key] !== serverRow[key]) {
                    patch[key] = serverRow[key];
                    changed = true;
                  }
                }
                if (changed) {
                  latestSheet.current = { ...cur, ...patch };
                  if (hookMountedRef.current) {
                    setState((s) =>
                      s.sheet ? { ...s, sheet: { ...s.sheet, ...patch } } : s,
                    );
                  }
                }
              }
              // #698 — flush() swallows a per-op failure internally (it marks
              // the outbox entry "error" or "terminal" and keeps draining
              // everything else); surface it here so a 409 (stale tab that
              // already confirmed/signed) reaches the UI instead of failing
              // silently.
              // #1605 — "terminal" included alongside "error": #1921 routed
              // every non-401 4xx (409, 403, validation) to status
              // "terminal", not "error". Gating on "error" alone (the
              // pre-#1921 check) silently dropped this banner for all of
              // them, including the 409/403 cases describeSaveError already
              // had dedicated copy for.
              const outboxOp = await getOutboxOp(
                `scoreSheet:${entityKey}`,
              ).catch(() => null);
              if (isMountedSheet() && hookMountedRef.current) {
                setState((s) => ({
                  ...s,
                  saveError: isSaveErrorOp(outboxOp?.status)
                    ? describeSaveError(outboxOp)
                    : null,
                }));
              }
            })
            .catch(() => {});
        }
        // Local commit is done either way: refresh the pending-sync badge
        // from the outbox now rather than waiting on the background flush.
        await syncEngine.refreshPending();
      };
      // Chain onto the previous save so enqueues never overlap.
      inflight.current = inflight.current.then(run, run);
      return inflight.current;
    },
    [rideId, judgeId]
  );

  // #2192 — the Start Scoring press. Reuses persist() as-is (it's already
  // the create-or-update path createScoreSheet/updateScoreSheet flow through,
  // via the outbox — see persist()'s own comment) so offline/outbox
  // semantics come for free: no separate "start" endpoint exists or is
  // needed, score-api's createScoreSheet already sets the ring's class/ride
  // if unset as a side effect of the PUT (recordRingScoringActivity, see
  // apps/score-api/src/lib/ring-state.js). Sets hasStarted optimistically
  // BEFORE the enqueue so ScoringForm unlocks the full form immediately
  // rather than waiting on the outbox flush round trip.
  // #2193 (its own card) wraps the click that calls this in a confirm
  // dialog; #2194 (its own card) restricts who can usefully press it. This
  // function itself stays exactly the unconditional "create/update the
  // sheet now" action both of those will call through to.
  // #2191 judge review — belt-and-braces: persist() silently refuses
  // (`if (!sheet || !judgeId) return inflight.current;`, no throw) whenever
  // judgeId is null, which is exactly the allowNoJudge/never-assigned case.
  // Flipping hasStarted optimistically in that case would still unlock the
  // full editable form (ScoringForm's `!isCompleted && !hasStarted` gate)
  // with nothing ever saved. verifyStillAssignedScribe's fail-closed fix
  // (ScoringForm.jsx) is the primary guard that keeps this call from ever
  // firing for that case; this is the second layer in case some other
  // caller reaches startScoring with no judgeId.
  const startScoring = useCallback(() => {
    if (!judgeId) return inflight.current;
    setState((s) => ({ ...s, hasStarted: true }));
    return persist(latestSheet.current);
  }, [persist, judgeId]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // #3008 round 11 -- null the ref out BEFORE persist() runs, the same
      // way every other saveTimer-clearing call site in this file already
      // does (startScoring/complete/confirm/sign all clear+null before their
      // own immediate persist). Without this, saveTimer.current stays a
      // stale truthy value forever after the debounce's first natural fire
      // (a timer id that already fired is still "truthy"), so the load
      // effect's unmount cleanup (this file's own #3008 round 10 flush)
      // misreads "a save fired and completed cleanly, ages ago" as "a save
      // is still pending" and fires a SECOND, stale, fire-and-forget
      // persist(latestSheet.current) on every unmount that ever scheduled a
      // save at all -- reintroducing exactly the outbox/cache pollution
      // round 10 was written to stop, just via a duplicate write instead of
      // a dropped one (confirmed: this duplicate write is what leaked one
      // test's edit into the next test's fresh render, since it resolves
      // asynchronously after the current test's own teardown).
      saveTimer.current = null;
      persist(latestSheet.current);
    }, SAVE_DEBOUNCE_MS);
  }, [persist]);

  // Single mutation path: derive the next sheet from the ref, update state, and
  // schedule a save. Using the ref (not state) as the source keeps the save
  // debounce correct across rapid edits.
  const applySheet = useCallback(
    (updater) => {
      const cur = latestSheet.current;
      if (!cur) return;
      const next = updater(cur);
      // #2810 — pre-flight on every mutation, against the SAME rule
      // score-api's updateScoreSheet 409s on (a frozen sheet, or a numeric
      // mark changed after marksConfirmedAt). Stored in a ref (not just
      // computed inside persist()) because persist() runs on a debounce
      // timer and only ever sees latestSheet.current at fire time, with no
      // way to recover what the PRECEDING sheet looked like by then — the
      // comparison has to happen here, at the point both ends of it are
      // still in hand.
      //
      // #2810 AC2 — judge the numeric-marks-frozen half of that verdict
      // against confirmedMarksRef's PINNED baseline, not against `cur`: `cur`
      // is the previous LOCAL edit, which already carries a blocked mark
      // change once one has been typed, so a later remarks-only edit's `cur`
      // vs `next` diff is remarks-only and reads as "no further change" —
      // clearing the block on a mark change that was never actually accepted.
      // Comparing against the sheet's marks as they stood AT marksConfirmedAt
      // instead means the verdict re-derives from scratch on every keystroke
      // and can't be cleared by an unrelated edit in between.
      const preflightExisting = confirmedMarksRef.current
        ? { ...cur, ...confirmedMarksRef.current }
        : cur;
      const verdict = preflightUpdateScoreSheet({ existing: preflightExisting, next });
      preflightBlockedRef.current = verdict.ok ? null : verdict;
      latestSheet.current = next;
      // #3008 round 6 -- every in-memory mutation of latestSheet.current
      // bumps the generation counter (see sheetGenerationRef's own comment).
      sheetGenerationRef.current += 1;
      // #3008 rule 3 -- flag this sheet as having an edit not yet enqueued,
      // BEFORE scheduling the debounced save. persist() clears this once the
      // edit is actually enqueued (see below); until then, db.js must never
      // treat a server-fresh row for this sheet as "no divergence".
      markUnsavedEdit(unsavedEntityKey());
      setState((s) =>
        s.sheet
          ? { ...s, sheet: next, preflightError: verdict.ok ? null : verdict }
          : s
      );
      scheduleSave();
    },
    [scheduleSave]
  );

  const setMovementScore = useCallback(
    (movementNumber, fields) => {
      applySheet((sheet) => {
        const existing = sheet.movementScores ?? [];
        const idx = existing.findIndex((m) => m.movementNumber === movementNumber);
        const next =
          idx >= 0
            ? existing.map((m, i) => (i === idx ? { ...m, ...fields, movementNumber } : m))
            : [...existing, { movementNumber, ...fields }];
        return { ...sheet, movementScores: next };
      });
    },
    [applySheet]
  );

  const setCollective = useCallback(
    (key, fields) => {
      applySheet((sheet) => {
        const prev = sheet.collectives?.[key] ?? {};
        return {
          ...sheet,
          collectives: { ...(sheet.collectives ?? {}), [key]: { ...prev, ...fields } },
        };
      });
    },
    [applySheet]
  );

  // #208 / #209 — merge arbitrary top-level sheet fields (eliminations,
  // errors, annotated prose, judgeSignature, …).
  const setSheetFields = useCallback(
    (fields) => applySheet((sheet) => ({ ...sheet, ...fields })),
    [applySheet]
  );

  // #266 — removing a point deduction or clearing a (non-Blood) elimination
  // requires a scribe-entered reason, forensically recorded. `changeReason`
  // rides the existing #571 audit pipeline: score-api reads it straight off
  // the PUT body (bypassing the schema, so no client regen) and stamps it on
  // the sheet item; the audit-stream consumer reads it into the WORM audit
  // trail (see docs/score/audit-design.md). It must NOT linger in local sheet
  // state past this one save — every subsequent debounced save resends the
  // WHOLE sheet, and a lingering reason would misattribute unrelated later
  // edits — so this bypasses the debounce (like complete()/confirmMarks())
  // and strips the reason back out of local state right after the send.
  const applySheetWithReason = useCallback(
    async (updater, reason) => {
      const cur = latestSheet.current;
      if (!cur) return;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const withReason = { ...updater(cur), changeReason: reason };
      latestSheet.current = withReason;
      // #3008 round 6 -- see sheetGenerationRef's own comment.
      sheetGenerationRef.current += 1;
      // #3008 rule 3 -- same flag as applySheet's, for the async gap between
      // this mutation and persist()'s own enqueue() call below (persist()
      // clears it).
      markUnsavedEdit(unsavedEntityKey());
      setState((s) => (s.sheet ? { ...s, sheet: withReason } : s));
      await persist(withReason);
      // #266 CI flake — build `cleared` from the exact object we just sent,
      // not from whatever latestSheet.current holds by now: a concurrent
      // write landing during the persist await (e.g. a prior confirmMarks()
      // call's #698 tail, which re-merges server state after its own flush)
      // can overwrite latestSheet.current with stale data that still
      // carries the just-removed content, and deriving `cleared` from THAT
      // silently resurrects it. If nothing else touched latestSheet.current
      // mid-flight, strip changeReason off withReason directly — the
      // removal survives no matter how slow persist was. If something did
      // race in, don't stomp its fields — just strip changeReason off
      // whatever is there now.
      const { changeReason: _omit, ...clearedFromSent } = withReason;
      const cleared =
        latestSheet.current === withReason
          ? clearedFromSent
          : { ...latestSheet.current, changeReason: undefined };
      latestSheet.current = cleared;
      setState((s) => (s.sheet ? { ...s, sheet: cleared } : s));
    },
    [persist]
  );

  const setGeneralRemarks = useCallback(
    (value) => applySheet((sheet) => ({ ...sheet, generalRemarks: value })),
    [applySheet]
  );

  // R1-UI-024 — captured arena-entry time in seconds (set on bell-stop).
  const setEntryTimeSeconds = useCallback(
    (seconds) => applySheet((sheet) => ({ ...sheet, entryTimeSeconds: seconds })),
    [applySheet]
  );

  // Sign + complete: flush the pending save (the sheet already carries the
  // judgeSignature via setSheetFields), then enqueue a completeScoreSheet op so
  // the server computes totals + stamps completedAt. Works offline — the op
  // resolves its server id from the cache once the create drains (the engine
  // remaps a temp ride's op after the ride syncs), so completion round-trips on
  // reconnect. Mark complete optimistically: the scribe has signed.
  const complete = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    // #3008 round 7 -- reserve the bump BEFORE calling persist(), and pass
    // the pre-bump value explicitly as the flush op's stamped generation
    // (see persist()'s own comment). This keeps sheetGenerationRef.current
    // already advanced for the entire async window between here and this
    // function's own second mutation below (including the `await
    // enqueue(...)` gap), so persist()'s own flush echo can never land
    // generation-matched and re-seat over an earlier, not-yet-flushed
    // mutation from a sibling call (the AC4 regression this round fixes).
    const opGeneration = sheetGenerationRef.current;
    sheetGenerationRef.current += 1;
    await persist(latestSheet.current, opGeneration);

    // #2810 — same SHEET_INCOMPLETE rule ScoringForm's handleConfirmScore
    // already catches at the field for the normal two-stage Confirm Score /
    // Complete & Sign flow (#2724); this is the belt-and-braces copy for the
    // OTHER path into complete() — the reopened-after-sign single "Save"
    // button (#901's handleSave), which never runs handleConfirmScore at
    // all. Blocked: skip the enqueue, surface preflightError, but the local
    // commit persist() just did above already landed (AC4).
    //
    // #2810 judge item 5 — klassRef.current is null when the class row
    // hasn't synced to this tablet yet (offline). effectiveTestForClass
    // treats an unknown class as an ordinary test (score-common's own
    // fallback, correct for score-api which always resolves classItem from
    // DynamoDB before calling it), so scoring against it here would run the
    // FULL per-movement/collective completeness check on what might really
    // be a championship/suitability placement-only sheet — a false client
    // block on an op the server would accept. Unknown class: don't guess,
    // don't block; the server still checks this for real once the op syncs.
    const completeVerdict = klassRef.current
      ? preflightConfirmOrCompleteScoreSheet({
          sheet: latestSheet.current,
          effectiveTest: effectiveTestForClass(testRef.current, klassRef.current),
          movementOverride: rideRef.current?.movementOverride ?? null,
        })
      : { ok: true };
    if (!completeVerdict.ok) {
      setState((s) => ({ ...s, preflightError: completeVerdict }));
      return;
    }

    const manual = getManualRideById(rideId);
    const effRideId = manual ? manual.serverId ?? rideId : rideId;
    const entityKey = `${effRideId}::${judgeId}`;
    await enqueue({
      opId: `complete:${entityKey}`,
      type: "completeScoreSheet",
      entityKey,
      tempId: manual && !manual.serverId ? effRideId : null,
      payload: { serverId: serverIdRef.current ?? null, rideId: effRideId, judgeId },
    });

    latestSheet.current = {
      ...latestSheet.current,
      completedAt: latestSheet.current?.completedAt ?? new Date().toISOString(),
    };
    // #3008 round 7 -- generation already bumped above, BEFORE persist(),
    // so this second optimistic mutation needs no bump of its own (see
    // persist()'s own comment for why the bump moved earlier).
    // #901 — wasEverSigned is monotonic/durable server-side too; setting it
    // here optimistically keeps it consistent with isCompleted.
    setState((s) => ({ ...s, isCompleted: true, wasEverSigned: true, sheet: latestSheet.current }));
    // #515 — isCompleted is already flipped optimistically above (the local
    // commit); don't hold the Sign/Save button's spinner on the network
    // flush that follows. Fire it in the background like persist() does.
    if (syncEngine.isOnline()) syncEngine.flush().catch(() => {});
    await syncEngine.refreshPending();
  }, [persist, rideId, judgeId]);

  // #698 — Confirm Score: the first of the two scoring stages. Flushes the
  // pending save (so the server confirms against the scribe's latest marks),
  // then enqueues a confirmScoreSheetMarks op through the SAME offline outbox
  // as every other mutation (createScoreSheet/updateScoreSheet/complete) —
  // no separate offline mechanism. One-way: there is no matching "unconfirm"
  // op anywhere in this file or the sync engine, by design (owner ruling).
  // Marked optimistically (isConfirmed true, marksConfirmedAt stamped) so the
  // FE freezes numeric entry immediately even offline; once online the
  // confirm-marks response (totalPoints/maxPoints/percentage) is pulled back
  // out of the cache the sync engine just wrote, so the real server totals
  // replace the optimistic guess.
  const confirmMarks = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    // #3008 round 7 -- see complete()'s own comment: reserve the bump BEFORE
    // persist() so sheetGenerationRef.current is already advanced across the
    // whole async window (including the `await enqueue(...)` below), closing
    // the gap where persist()'s own flush echo could otherwise land
    // generation-matched before this function's own second mutation runs.
    const opGeneration = sheetGenerationRef.current;
    sheetGenerationRef.current += 1;
    await persist(latestSheet.current, opGeneration);

    const manual = getManualRideById(rideId);
    const effRideId = manual ? manual.serverId ?? rideId : rideId;
    const entityKey = `${effRideId}::${judgeId}`;
    await enqueue({
      opId: `confirmMarks:${entityKey}`,
      type: "confirmScoreSheetMarks",
      entityKey,
      tempId: manual && !manual.serverId ? effRideId : null,
      payload: { serverId: serverIdRef.current ?? null, rideId: effRideId, judgeId },
    });

    latestSheet.current = {
      ...latestSheet.current,
      marksConfirmedAt: latestSheet.current?.marksConfirmedAt ?? new Date().toISOString(),
    };
    // #3008 round 7 -- generation already bumped above, BEFORE persist().
    // #2810 AC2 — pin the confirmed-marks baseline at THIS moment, the first
    // time this sheet is confirmed (same guard as apply()'s own sync, above).
    if (
      confirmedMarksRef.current?.marksConfirmedAt !== latestSheet.current.marksConfirmedAt
    ) {
      confirmedMarksRef.current = {
        marksConfirmedAt: latestSheet.current.marksConfirmedAt,
        movementScores: latestSheet.current.movementScores,
        collectives: latestSheet.current.collectives,
      };
    }
    setState((s) => ({ ...s, isConfirmed: true, sheet: latestSheet.current }));

    // #515 — isConfirmed is already flipped optimistically above; the real
    // server totals are a nice-to-have refinement, not something the Confirm
    // button's spinner should block on. Pull them back in the background
    // once flush lands instead of awaiting the network round-trip here.
    if (syncEngine.isOnline()) {
      syncEngine
        .flush()
        .then(async () => {
          const after = await scoreSheets
            .byRideJudge(effRideId, judgeId)
            .catch(() => []);
          // #2756 — never merge the provisional entityKey-keyed row back into
          // the mounted sheet: it would drag `id: "rideId::judgeId"` and
          // `provisional: true` into sheet state (and onward into the next
          // save's body). Only a real server row carries the totals this
          // merge exists to pick up.
          // #2972 - nor a stranger's contender: merging its totals into the
          // mounted sheet would show this scribe numbers off a sheet no
          // other reader treats as this ride's real score.
          // #2972 judge round 3 (driver rule) - THIS device's own contender
          // resolves FIRST: an authoritative-first pick spread the race
          // WINNER's whole sheet (its id included) into latestSheet, so this
          // scribe then saw, and on the next save sent, the winner's sheet.
          const { row: server } = await resolveOwnCachedServerRow(
            after,
            entityKey,
            judgeId,
          );
          if (server) {
            latestSheet.current = { ...latestSheet.current, ...server };
            setState((s) => ({ ...s, sheet: latestSheet.current }));
          }
        })
        .catch(() => {});
    }
    await syncEngine.refreshPending();
  }, [persist, rideId, judgeId]);

  // #1751 — one panel judge signs THIS row's sheet, as part of the class-level
  // signing ceremony (docs/score/equitation-class-sheet-design.md §6). Unlike
  // complete(), this is NOT a finalization: a DSM sheet takes three of these,
  // one per seated judge, and only then will completeScoreSheet stop refusing
  // (#1748's panel gate).
  //
  // Flushes the pending save first, for the same reason confirmMarks() does:
  // the server refuses to sign a sheet whose marks are not confirmed, and the
  // confirm that precedes this needs the scribe's latest cells to have landed.
  //
  // The optimistic local merge uses score-common's mergePanelSignature — the
  // same function the server applies — so an offline signature shows up in the
  // grid immediately and in exactly the shape the drained response will
  // replace it with. `signatureHash` is deliberately null in the optimistic
  // entry: the real hash is the S3 content address of the judge's stored
  // signature, which only the server can know. Nothing in scribe renders the
  // hash; the grid only asks WHICH judges have signed.
  const signPanel = useCallback(
    async (panelJudgeId) => {
      if (!panelJudgeId) return;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      // #3008 round 7 -- see complete()'s own comment: reserve the bump
      // BEFORE persist() so sheetGenerationRef.current is already advanced
      // across the whole async window (including the `await enqueue(...)`
      // below), closing the gap where persist()'s own flush echo could
      // otherwise land generation-matched and re-seat latestSheet.current
      // (dropping signatures, which a PUT echo never carries) BEFORE this
      // function's own merge below runs -- the AC4 regression (two panel
      // judges signing the same row in sequence): judge-b's own flush
      // landing in that gap wiped judge-a's already-merged signature right
      // before judge-b's merge ran on top of the now-emptied base.
      const opGeneration = sheetGenerationRef.current;
      sheetGenerationRef.current += 1;
      await persist(latestSheet.current, opGeneration);

      const manual = getManualRideById(rideId);
      const effRideId = manual ? manual.serverId ?? rideId : rideId;
      const entityKey = `${effRideId}::${judgeId}`;
      await enqueue({
        // Per JUDGE as well as per sheet: two judges signing the same row are
        // two separate ops, and a re-sign by the same judge replaces its own
        // queued op rather than stacking a second one.
        opId: `panelSign:${entityKey}:${panelJudgeId}`,
        type: "panelSignScoreSheet",
        entityKey,
        tempId: manual && !manual.serverId ? effRideId : null,
        payload: {
          serverId: serverIdRef.current ?? null,
          rideId: effRideId,
          judgeId,
          panelJudgeId,
        },
      });

      latestSheet.current = {
        ...latestSheet.current,
        signatures: mergePanelSignature(latestSheet.current?.signatures, {
          judgeId: panelJudgeId,
          signatureHash: null,
          signedAt: new Date().toISOString(),
        }),
      };
      // #3008 round 7 -- generation already bumped above, BEFORE persist().
      setState((s) => (s.sheet ? { ...s, sheet: latestSheet.current } : s));

      if (syncEngine.isOnline()) syncEngine.flush().catch(() => {});
      await syncEngine.refreshPending();
    },
    [persist, rideId, judgeId]
  );

  return {
    ...state,
    setMovementScore,
    setCollective,
    setSheetFields,
    setGeneralRemarks,
    setEntryTimeSeconds,
    applySheetWithReason,
    complete,
    confirmMarks,
    signPanel,
    startScoring,
  };
}
